import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsAPI } from '../../../lib/api';

export function useUnreadCount(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['notifications', 'unread-count', organizationId],
    queryFn: async () => {
      if (!organizationId) return { count: 0 };
      const { data } = await notificationsAPI.getUnreadCount(organizationId);
      return data;
    },
    enabled: !!organizationId,
    refetchInterval: 30_000,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

export function useNotificationList(
  organizationId: string | undefined,
  options?: { category?: string; unreadOnly?: boolean },
) {
  return useInfiniteQuery({
    queryKey: ['notifications', 'list', organizationId, options?.category, options?.unreadOnly],
    queryFn: async ({ pageParam }) => {
      if (!organizationId) return { notifications: [], hasMore: false };
      const { data } = await notificationsAPI.list({
        organizationId,
        category: options?.category,
        unreadOnly: options?.unreadOnly,
        limit: 20,
        cursor: pageParam,
      });
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => notificationsAPI.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (organizationId: string) => notificationsAPI.markAllAsRead(organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
